<template>
  <v-flex pa-2 xs12>
    <v-card
      :id="`${parentContextId}_Card`"
      class="sb_component_card"
      height="100%"
    >
      <v-card-title class="sb_component_card_title">
        <!-- title -->
        <v-flex xs12 md6 lg9>
          <v-tooltip top nudge-right="30">
            <template v-slot:activator="{ on, attr }">
              <div class="d-inline-flex align-center GSSecHeadingCol" wrap>
                <v-icon
                  v-if="moduleIcon"
                  :class="[
                    'mr-2',
                    'material-icons-outlined',
                    moduleIcon,
                    moduleTooltips[moduleIcon] ? 'module-icon' : '',
                  ]"
                  v-bind="attr"
                  v-on="on"
                >
                  {{ moduleIcon }}
                </v-icon>
                <h4 :class="titleClass">
                  {{ title }}
                </h4>
                <slot name="config" />
                <slot name="after-title" />
              </div>
            </template>
            <span :id="`${parentContextId}_Icon_Tooltip`">{{
              moduleTooltips[moduleIcon]
            }}</span>
          </v-tooltip>
        </v-flex>
        <v-flex v-if="lastReportRefresh" xs12 md6 lg9>
          <h5 class="last-refresh">
            Last Refreshed On: {{ lastReportRefresh }} (UTC)
          </h5>
        </v-flex>
        <!-- card buttons -->
        <v-flex xs12 md6 lg3 class="Global-Panel-Content">
          <v-layout row wrap justify-end>
            <div
              class="
                UDM_GS_HeaderIcons
                d-inline-flex
                align-center
                card-header-actions
              "
            >
              <slot name="actions" />

              <move-card-up-down
                v-if="displayOrder > 0"
                :id-prefix="parentContextId"
                @MoveUp="$emit('MoveCardUp', displayOrder)"
                @MoveDown="$emit('MoveCardDown', displayOrder)"
              />
              <pin-unpin-button
                v-if="cardConfig.pin"
                :card-name="parentContextId"
                :id-prefix="parentContextId"
                :pin-parameters="pinParameters"
                @pinned="(v) => $emit('pinned', v)"
                @pin-error="
                  (e) => errors.push(`Error pinning card: ${e.message}`)
                "
                @try-pin="
                  errors = [];
                  $emit('try-pin');
                "
              />
              <quick-help-button
                v-if="cardConfig.help && !hideHelpIcon"
                :user-guide-identifier="userGuideIdentifier"
                :id-prefix="parentContextId"
              />
              <span
                v-if="cardConfig.minimize && !isInPanel"
                class="text-center card-expand-collapse-button"
              >
                <v-btn
                  :id="`${parentContextId}-btn-expand`"
                  icon
                  flat
                  depressed
                  fab
                  @click="toggleCard"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attr }">
                      <v-icon
                        :class="{
                          'material-icons-round': true,
                        }"
                        v-bind="attr"
                        v-on="on"
                      >
                        {{ isCardExpanded ? 'expand_less' : 'expand_more' }}
                      </v-icon>
                    </template>
                    <span :id="`${parentContextId}_expand_tooltip`">{{
                      isCardExpanded ? 'Collapse this card' : 'Expand this card'
                    }}</span>
                  </v-tooltip>
                </v-btn>
              </span>
              <v-list-item
                v-for="item in actionButtons"
                :key="item.event"
                class="text-center"
              >
                <v-btn
                  :id="`${parentContextId}-btn-${item.icon}`"
                  :disabled="item.disabled"
                  icon
                  flat
                  depressed
                  fab
                  @click="item.onClick"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attr }">
                      <v-icon
                        :class="`material-icons-round ${item.className}`"
                        v-bind="attr"
                        v-on="on"
                      >
                        {{ item.icon }}
                      </v-icon>
                    </template>
                    <span>{{ item.buttonName }}</span>
                  </v-tooltip>
                </v-btn>
              </v-list-item>
              <v-btn
                v-for="(btn, idx) in panelButtons"
                :key="idx"
                :class="btn.className"
                icon
                flat
                depressed
                fab
                @click="btn.onClick"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on, attr }">
                    <v-icon
                      :class="`material-icons-round ${btn.className}`"
                      v-bind="attr"
                      v-on="on"
                    >
                      {{ btn.icon }}
                    </v-icon>
                  </template>
                  <span>{{ btn.buttonName }}</span>
                </v-tooltip>
              </v-btn>
            </div>
          </v-layout>
        </v-flex>
        <v-layout row wrap justify-start>
          <v-flex xs12 md6 lg4>
            <slot name="below-title" />
          </v-flex>
        </v-layout>
      </v-card-title>
      <div
        :data-cardHeight="cardHeight"
        :class="{
          'sb_component_card_body Global-Panel-Content': true,
          'card-collapsed': !isCardExpanded,
          'card-transitioning': isTransitioning,
        }"
      >
        <v-card-text
          :class="{
            'pt-1': cardConfig.filter || cardConfig.download,
            'h-100-p': $slots.sphereTable,
          }"
        >
          <v-alert
            :value="longLoadTime && loading"
            color="info"
            class="sb_component_card_time_loader"
            outline
          >
            {{ currentLoadingMessage }}
          </v-alert>
          <v-progress-linear
            v-if="initialLoadComplete && loading"
            class="sb_component_card_loader_bar"
            :indeterminate="true"
          ></v-progress-linear>
          <status-display-bar :error="errors" />
          <div class="p-relative" :class="{ 'h-100-p': $slots.sphereTable }">
            <div v-if="initialLoadComplete && loading" class="sb-overlay"></div>
            <slot name="top-of-body" />
            <v-layout row wrap :class="{ 'h-100-p': $slots.sphereTable }">
              <v-flex
                v-if="!!$slots.chart && !loadingError"
                :class="{
                  'pr-2': hasTableSlot,
                  xs12: true,
                  sm12: true,
                  md5: hasTableSlot,
                  md12: !hasTableSlot,
                  'slot-chart': true,
                }"
              >
                <skeleton-loader :loading="!initialLoadComplete" type="chart">
                  <slot name="chart" />
                </skeleton-loader>
              </v-flex>
              <v-flex
                v-if="!!($slots.table || $slots.sphereTable) && !loadingError"
                :class="{
                  'pl-2': hasChartSlot,
                  xs12: true,
                  sm12: true,
                  md7: hasChartSlot,
                  md12: !hasChartSlot,
                  'slot-table': true,
                }"
              >
                <slot name="sphereTable" />
                <skeleton-loader
                  v-if="$slots.table"
                  :loading="!initialLoadComplete"
                  type="table"
                >
                  <slot name="table" />
                </skeleton-loader>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>
      </div>
    </v-card>
  </v-flex>
</template>

<script>
import QuickHelpButton from './QuickHelpButton.vue';
import PinUnpinButton from './CardPinUnpinButton.vue';
import MoveCardUpDown from './MoveCardUpDown.vue';
import StatusDisplayBar from './StatusDisplayBar.vue';
import SkeletonLoader from './SkeletonLoader.vue';

const CARD_TYPES = {
  /** The most common use case for cards - showing a chart/table and pinnable */
  pinnable: {
    close: false,
    collapse: false,
    customize: false,
    help: true,
    minimize: true,
    pin: true,
  },
  /** A more basic card that can't be pinned, usually showing contextual info */
  nonpinnable: {
    close: false,
    collapse: false,
    customize: false,
    help: true,
    minimize: true,
    pin: false,
  },
};

const DEFAULT_ERROR_MESSAGE =
  'SPHEREboard reported an error, please reach out to your SPHEREboard Administrator or raise a Support Portal request';

const CUSTOM_MESSAGE_BASE =
  'SPHEREboard reported an error while loading this card:';

export default {
  name: 'BaseCard',
  components: {
    QuickHelpButton,
    PinUnpinButton,
    MoveCardUpDown,
    StatusDisplayBar,
    SkeletonLoader,
  },
  props: {
    displayOrder: {
      type: Number,
      default: undefined,
    },
    /** The text that appears in the card header */
    title: {
      type: String,
      required: true,
    },
    titleClass: {
      type: String,
      default: 'sb-header',
    },
    /**
     * The tag to link to the relevant section of the User Guide for this card
     */
    moduleIcon: {
      type: String,
      default: '',
    },
    /**
     * An identifier for the card, this is used to create IDs for selectors, and
     * also passed to the "pin/unpin" component as the card name
     */
    idTag: {
      type: String,
      default() {
        return undefined;
      },
    },
    /** icon redirect to SB settings */
    configSettings: {
      type: Boolean,
      default: false, // Default value set to false
    },
    /**
     * Represents the general use case of the card - this is used to determine
     * what type of icon buttons are shown in the header
     */
    cardType: {
      type: String,
      default: undefined, //TODO: make a required prop after implementing everywhere
      validator(value) {
        return Object.keys(CARD_TYPES).includes(value);
      },
    },
    userGuideIdentifier: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Parameters to store in the DB with the pinned card, if any are needed to
     * save some context for rendering on the dashboard
     */
    pinParameters: {
      type: Object,
      default: () => ({}),
    },
    /**
     * A list of API endpoints that this card should watch to determine loading
     * and error states.
     */
    apiEndpoints: {
      type: Array,
      default: () => [],
      validator(value) {
        if (!(value instanceof Array)) return false;
        return value.filter((v) => typeof v !== 'string').length === 0;
      },
    },
    fullHeightCard: {
      type: Boolean,
    },
    lastReportRefresh: {
      type: String,
      default: '',
    },
    hideHelpIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const loadingMessages = [
      'Combing through credentials. No shortcuts taken here.✅',
      'Running the numbers on your identity footprint️…just about done 🏃️‍♀',
      "Assembling your security landscape. It's a big one.",
      'Ingesting data at a compliant pace.🔐',
      'Your environment has a lot going on. Give us a moment.',
      'Giving your identity a quick spa treatment—exfoliating bad access ✨',
      'Scrubbing your identity for maximum shine…almost done ✨',
      'Polishing your security posture—almost there!',
      'Freshening your identity hygiene—minty clean security incoming!🔐',
      'Shielding your account from shady vibes… almost done!🤺',
      'Guarding your credentials—🛡 because you’re worth it.',
      'Checking all the locks—double, triple, SPHERE style.',
      'Herding your identities into a nice, safe corral 🐑🔒',
      'Helping you keep bad actors out. Just a sec. 🤺',
      'Loading your data and locking down stray permissions.🔐',
      'Refreshing info and tightening security bolts.🔐',
      'Fetching your identity data and scrubbing for risks.',
      'Loading your data and evicting shady permissions. Security bouncer mode activated.',
      'Pulling in your identity data while shooing risky access out the door…',
      'Compiling your details and de‑gunking your identity—spa day for your security.💪',
      'Retrieving your data and sweeping identity corners for forgotten privileges…',
      'Warming up your data and scrubbing security surfaces until they sparkle.✨',
      'Checking your data and hardening identity defenses—nearly there.💪',
      'Preparing your data and sealing any loose credential gaps.',
      'Organizing your data and strengthening your identity hygiene.',
    ];
    return {
      moduleTooltips: {
        push_pin: 'Dashboard',
        snippet_folder: 'Unstructured Data',
        account_circle: 'Accounts',
        rule: 'Asset Review',
        groups: 'Active Directory Groups',
        badge: 'Identity Access Management',
        cloud: 'Cloud Data',
        bar_chart: 'Report Center',
        handyman: 'Administration',
        menu_book: 'Confluence',
        manage_accounts: 'Ownership Management',
      },
      isCardExpanded: true,
      cardHeight: 0,
      isTransitioning: false,
      buttons: {
        deleteColumns: {
          buttonName: 'Delete',
          icon: 'delete_forever',
          onClick: () => this.$emit('move-down'),
        },
        customSettings: {
          className: 'custom-settings',
          buttonName: 'CustomSettings',
          icon: 'settings',
          onClick: () => this.$emit('messageMe'),
        },
        collapse: {
          className: 'collapse-panel',
          buttonName: 'Collapse this panel',
          icon: 'keyboard_double_arrow_left',
          onClick: () => this.$emit('collapse'),
        },
        close: {
          className: 'close-panel',
          buttonName: 'Close this panel',
          icon: 'close',
          onClick: () => this.$emit('close'),
        },
      },
      // a list of error messages for whatever errors occur
      errors: [],
      loadingMessages,
      currentMessageIndex: Math.floor(Math.random() * loadingMessages.length),
      messageIntervalId: null,
      // if the card has been waiting too long for the initial data load
      longLoadTime: false,
      loadingCounts: {},
      successCounts: {},
      errorCounts: {},
      initialLoad: {
        startTime: undefined,
        complete: false,
        longLoadTimeLimitMs: 10000,
      },
      /** The closest parent horizontal-expansion-panel, if it exists */
      closestPanel: undefined,
    };
  },
  computed: {
    actionButtons() {
      //TODO: pin/unpin buttons could go in here, but they have a fair amount
      // of logic in them
      let buttons = [];
      const { deleteColumns, customSettings, collapse } = this.buttons;
      if (this.cardConfig.customize) {
        buttons.push(deleteColumns, customSettings);
      }
      if (this.cardConfig.collapse) {
        buttons.push(collapse);
      }
      return buttons;
    },
    /** If this card is inside a horizontal-expansion-panel */
    isInPanel() {
      return this.closestPanel instanceof Element;
    },
    panelButtons() {
      if (!this.isInPanel) {
        return [];
      }
      const { collapse, close } = this.buttons;
      return [collapse, close];
    },
    hasChartSlot() {
      return !!this.$slots.chart;
    },
    hasTableSlot() {
      return !!(this.$slots.table || this.$slots.sphereTable);
    },
    /**
     * Identifies this base-card from others, typically this is the name of the
     * parent component but could be any string
     */
    parentContextId() {
      if (this.idTag !== undefined) {
        return this.idTag;
      }
      const parentTag = this.$parent.$vnode.tag;
      return parentTag.split('-').reverse()[0];
    },
    /**
     * An object containing booleans for showing/hiding icon buttons in the card
     * header. When a `cardType` is supplied it will override all the other show
     * and hide button props
     */
    cardConfig() {
      return {
        ...CARD_TYPES[this.cardType],
        close: this.isInPanel && this.$listeners.close !== undefined,
        collapse: !this.isInPanel && this.$listeners.collapse !== undefined,
      };
    },
    loadingError() {
      return Object.values(this.errorCounts).filter((v) => v > 0).length > 0;
    },
    /**
     * If any of the provided `apiEndpoints` are currently loading
     */
    loading() {
      return Object.values(this.loadingCounts).filter((v) => v > 0).length > 0;
    },
    /**
     * If the initial batch of requests has finished loading
     */
    initialLoadComplete() {
      return this.initialLoad.complete || !this.loading;
    },
    currentLoadingMessage() {
      return this.loadingMessages[this.currentMessageIndex];
    },
  },
  watch: {
    longLoadTime(val) {
      if (val) {
        this.messageIntervalId = setInterval(() => {
          this.currentMessageIndex =
            (this.currentMessageIndex + 1) % this.loadingMessages.length;
        }, 5000);
      } else {
        clearInterval(this.messageIntervalId);
        this.currentMessageIndex = Math.floor(
          Math.random() * this.loadingMessages.length
        );
      }
    },
    loading(val) {
      if (!val) {
        clearInterval(this.messageIntervalId);
        this.currentMessageIndex = Math.floor(
          Math.random() * this.loadingMessages.length
        );
      }
    },
    isCardExpanded: function (p) {
      this.$emit('isPanelOpen', p);
    },
    isTransitioning: function (val) {
      if (!val) {
        document.querySelector(
          '#' + this.parentContextId + '_Card .sb_component_card_body'
        ).style.height = null;
      }
    },
  },
  mounted() {
    if (this.fullHeightCard) this.setBaseCardHeight();
    this.closestPanel = this.$el.closest('.horizontal-expansion-panel');
    if (this.apiEndpoints.length === 0) {
      this.initialLoad.startTime = Date.now();
      this.initialLoad.complete = true;
    }
    for (let endpoint of this.apiEndpoints) {
      this.registerEndpointInterceptor(
        endpoint,
        (config) => {
          if (!this.loading) {
            this.errors = [];
          }
          if (this.loadingCounts[endpoint] === undefined) {
            this.$set(this.loadingCounts, endpoint, 0);
          }
          this.loadingCounts[endpoint] += 1;
          if (this.initialLoad.startTime === undefined) {
            this.initialLoad.startTime = Date.now();
          }
          console.debug(`'${this.title}' sent '${config.url}'`);
        },
        ({ data, config }) => {
          let isErrorIndicatedInResponse = (responseBody) => {
            // These properties are all lowercase for normalization
            let errorIndicatorKeys = [
              'error',
              'errorstatus',
              'errormessage',
              'errors',
            ];
            let errorIndicated = false;

            if (
              responseBody === false ||
              responseBody.status === false ||
              responseBody.success === false ||
              responseBody.Success === false
            ) {
              errorIndicated = true;
            }

            // See if the response body includes a property (normalized to lowercase) indicating an error
            else if (
              Object.keys(responseBody).some((key) => {
                // This code is for the empty "errors" array use case, which does not indicate an error
                if (
                  responseBody[key] == null ||
                  (Array.isArray(responseBody[key]) &&
                    responseBody[key].length === 0)
                ) {
                  return false;
                }
                return errorIndicatorKeys.includes(key.toLowerCase());
              })
            ) {
              errorIndicated = true;
            }

            return errorIndicated;
          };

          let getErrorsFromResponseBody = (responseBody) => {
            if (responseBody.errorMessage && responseBody.errorMessage != '') {
              return responseBody.errorMessage;
            }
            if (responseBody.ErrorMessage && responseBody.ErrorMessage != '') {
              return responseBody.ErrorMessage;
            }
            return undefined;
          };

          if (this.successCounts[endpoint] === undefined) {
            this.$set(this.successCounts, endpoint, 0);
          }
          this.successCounts[endpoint] += 1;
          this.loadingCounts[endpoint] -= 1;
          if (!this.loading) {
            this.initialLoad.complete = true;
          }
          console.debug(`'${this.title}' loaded '${config.url}'.`);
          let cardErrorMessage = '';
          // Handle a potential error message hidden in a 2xx response
          if (isErrorIndicatedInResponse(data)) {
            cardErrorMessage = DEFAULT_ERROR_MESSAGE;

            let endpointErrorMessage = getErrorsFromResponseBody(data);

            if (typeof endpointErrorMessage === 'string') {
              cardErrorMessage = `${CUSTOM_MESSAGE_BASE} ${endpointErrorMessage}`;
            }

            if (this.errorCounts[endpoint] === undefined) {
              this.$set(this.errorCounts, endpoint, 0);
            }
            this.errorCounts[endpoint] += 1;
            this.loadingCounts[endpoint] -= 1;
            this.errors.push(cardErrorMessage);
          }
        },
        (error) => {
          if (this.errorCounts[endpoint] === undefined) {
            this.$set(this.errorCounts, endpoint, 0);
          }
          this.errorCounts[endpoint] += 1;
          this.loadingCounts[endpoint] -= 1;
          this.errors.push(DEFAULT_ERROR_MESSAGE);
          console.error(
            `'${this.title}' failed to load '${error.config.url}'.`,
            error
          );
        }
      );
    }
    const intervalId = setInterval(() => {
      if (this.initialLoad.complete) {
        clearInterval(intervalId);
      }
      if (this.initialLoad.startTime !== undefined) {
        const timeDiffMs = Date.now() - this.initialLoad.startTime;
        if (timeDiffMs > this.initialLoad.longLoadTimeLimitMs) {
          this.longLoadTime = true;
          clearInterval(intervalId);
        }
      }
    }, 500);
  },
  methods: {
    toggleCard() {
      this.isTransitioning = true;
      let cardBodyElement = document.querySelector(
        '#' + this.parentContextId + '_Card .sb_component_card_body'
      );
      this.cardHeight = cardBodyElement.clientHeight;
      let previousHeight = cardBodyElement.getAttribute('data-cardHeight');
      cardBodyElement.style.height = this.isCardExpanded
        ? this.cardHeight + 'px'
        : previousHeight + 'px';
      this.isCardExpanded = !this.isCardExpanded;
      setTimeout(() => {
        this.isTransitioning = false;
      }, 200);
    },

    setBaseCardHeight() {
      let tablePositionTop = this.$el
        .getElementsByClassName('sb_component_card_body')[0]
        .getBoundingClientRect().top;
      let applicationFooterHeight =
          document.querySelector('footer.v-footer').clientHeight,
        spacingBelowCard = 16;
      let maxHeight =
        'calc(100vh - ' +
        (tablePositionTop + applicationFooterHeight + spacingBelowCard) +
        'px)';
      this.$el.getElementsByClassName(
        'sb_component_card_body'
      )[0].style.height = maxHeight;
    },
    /**
     * Called externally by parent
     * @param {Error} error
     * @param {Boolean} useCustomMessage
     *  - use server response error message instead of generic default
     *  - (default false)
     */
    setError(error, useCustomMessage = false) {
      this.errors = useCustomMessage
        ? [`${CUSTOM_MESSAGE_BASE} ${error.message}`]
        : [DEFAULT_ERROR_MESSAGE];
    },
  },
};
</script>
<style scoped>
.sb_component_card_body {
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}
.sb_component_card_body.card-collapsed:not(.card-transitioning) {
  height: 0;
  visibility: hidden;
}
.card-expand-collapse-button {
  line-height: 1;
}
.sb_component_card .UDM_GS_HeaderIcons .card-expand-collapse-button .v-icon {
  font-size: 24px;
}
.sb_component_card
  .sb_component_card_title
  .card-header-actions
  >>> .v-btn.v-btn--icon:hover:before,
.sb_component_card
  .sb_component_card_title
  .card-header-actions
  >>> .v-btn.v-btn--icon:focus:before {
  border: 2px solid;
  opacity: 1;
  background-color: transparent;
}
.sb_component_card
  .sb_component_card_title
  .card-header-actions
  >>> .v-btn.v-btn--icon:before {
  opacity: 0.12;
  background-color: currentColor;
}
.sb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 8;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
}
.module-icon {
  color: #a6acb0;
}
.last-refresh {
  color: #b6b6b7;
  font-size: 12px;
}
</style>
